import cx from 'classnames';
import * as React from 'react';
import { callAll } from '../helper/fp';
import { useId } from '../helper/hooks';
import { isBool, isDefined } from '../helper/typeguard';
import { Tooltip } from '../Tooltip/Tooltip';
import { RadioControlContext } from './RadioControlContext';

export interface RadioInputProps extends Omit<JSX.IntrinsicElements['input'], 'value'> {
  label: React.ReactNode;
  value: string | number | boolean;
  /** validation status of the field */
  status?: 'error' | 'success';
  tooltip?: React.ReactNode;
}

/**
 * `RadioInput` can be used in conjection with `RadioControlGroup` or as standalone
 *  When used as standalone, you can pass down any props a radio input accepts and it would just works.
 */
export const RadioInput: React.FC<RadioInputProps> = (props) => {
  const { label, onChange, checked, id: providedId, value, status, tooltip, ...inputProps } = props;
  const id = useId(providedId);

  const { value: contextValue, onChangeValue, name, status: contextStatus, tooltip: contextTooltip } = React.useContext(
    RadioControlContext
  );
  const isChecked = isDefined(checked) ? checked : value === contextValue;
  const radioStatus = status || contextStatus;
  const renderedTooltip = isDefined(tooltip) ? tooltip : contextTooltip;
  const inputNode = (
    <input
      {...inputProps}
      name={inputProps.name || name}
      value={!isBool(value) ? value : undefined} // we pass down value only if it's not boolean because boolean is not valid input value
      type="radio"
      id={id}
      className="visuallyhidden"
      onChange={callAll(onChange, () => onChangeValue(value))}
      checked={isChecked}
      aria-checked={isChecked}
    />
  );

  return (
    <div className="radio-group-item">
      {renderedTooltip ? (
        <Tooltip label={renderedTooltip} avoidPositions={['right']}>
          {inputNode}
        </Tooltip>
      ) : (
        inputNode
      )}
      <label
        htmlFor={id}
        className={cx('radio', inputProps.disabled && 'disabled', radioStatus && `has-${radioStatus}`)}
      >
        {label}
      </label>
    </div>
  );
};
